import type { AppEvent } from "../api";

/** Enough to refresh a row in place when cookies land without bumping sort. */
function contentSig(e: AppEvent): string {
  const raw = e.payload;
  const pull =
    raw && typeof raw === "object" && "original" in raw && (raw as { original?: unknown }).original
      ? (raw as { original: Record<string, unknown> }).original
      : raw && typeof raw === "object"
        ? (raw as Record<string, unknown>)
        : null;
  if (!pull) return e.status;
  const cookies = String(pull.cookies || "");
  const events = Array.isArray(pull.events) ? pull.events.length : 0;
  const alert =
    pull.operatorAlert && typeof pull.operatorAlert === "object"
      ? String((pull.operatorAlert as { type?: string }).type || "")
      : "";
  const gql =
    pull.graphql && typeof pull.graphql === "object"
      ? (pull.graphql as { expenses?: unknown })
      : null;
  const expenses = Array.isArray(gql?.expenses) ? gql.expenses.length : 0;
  return `${e.status}|${pull.collectorStatus || ""}|${pull.collectorPhase || ""}|c${cookies.length}|e${events}|${alert}|x${expenses}`;
}

type Listener = (events: AppEvent[]) => void;
type StatusListener = (info: {
  status: "connecting" | "open" | "reconnecting" | "closed";
  error: string | null;
}) => void;
type AssignmentListener = (msg: {
  type: "assignment" | "unassignment";
  resourceKey: string;
  message: string;
  assignedKeys: string[];
  events?: AppEvent[];
  eventId?: number;
}) => void;
type DiffListener = (changed: AppEvent[], source: "catchup" | "live") => void;

const CATCHUP_REASONS = new Set(["catchup", "bootstrap"]);

/**
 * Single shared local-DB feed for Electron.
 * Avoids N× listEvents(2000) when many pages + Station each subscribe to db-changed.
 */
class DesktopEventsHub {
  private events: AppEvent[] = [];
  private listeners = new Set<Listener>();
  private statusListeners = new Set<StatusListener>();
  private assignmentListeners = new Set<AssignmentListener>();
  private diffListeners = new Set<DiffListener>();
  private seen = new Map<number, string>();
  private initialDone = false;
  private started = false;
  private reloadTimer: ReturnType<typeof setTimeout> | null = null;
  private reloading = false;
  private dirty = false;
  private pendingSource: "catchup" | "live" = "catchup";
  private lastFingerprint = "";
  private unsubs: Array<() => void> = [];
  private status: "connecting" | "open" | "reconnecting" | "closed" = "closed";
  private error: string | null = null;
  private gen = 0;

  getSnapshot(): AppEvent[] {
    return this.events;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    listener(this.events);
    this.ensureStarted();
    return () => {
      this.listeners.delete(listener);
    };
  }

  subscribeStatus(listener: StatusListener): () => void {
    this.statusListeners.add(listener);
    listener({ status: this.status, error: this.error });
    this.ensureStarted();
    return () => this.statusListeners.delete(listener);
  }

  subscribeAssignment(listener: AssignmentListener): () => void {
    this.assignmentListeners.add(listener);
    this.ensureStarted();
    return () => this.assignmentListeners.delete(listener);
  }

  /** Station / side-effects: only newly changed rows */
  subscribeDiffs(listener: DiffListener): () => void {
    this.diffListeners.add(listener);
    this.ensureStarted();
    return () => this.diffListeners.delete(listener);
  }

  private setStatus(
    status: "connecting" | "open" | "reconnecting" | "closed",
    error: string | null = this.error
  ): void {
    if (this.status === status && this.error === error) return;
    this.status = status;
    this.error = error;
    for (const l of this.statusListeners) {
      try {
        l({ status, error });
      } catch {
        /* ignore */
      }
    }
  }

  private ensureStarted(): void {
    if (this.started || !window.brexDesktop?.listEvents) return;
    this.started = true;
    this.setStatus("connecting", null);
    const myGen = ++this.gen;

    void this.reload().then(() => {
      if (myGen !== this.gen) return;
      this.setStatus("open", null);
    });

    const unDb = window.brexDesktop.onDbChanged?.((info) => {
      this.noteReason(info?.reason);
      this.scheduleReload();
    });
    const unSync = window.brexDesktop.onSyncStatus?.((s) => {
      if (s.online) this.setStatus("open", s.error);
      else if (s.syncing) this.setStatus("connecting", s.error);
      else if (
        s.error &&
        (/sign in/i.test(s.error) || /session expired/i.test(s.error))
      ) {
        this.setStatus("closed", s.error);
      } else if (s.error && /reconnect/i.test(s.error)) {
        this.setStatus("reconnecting", s.error);
      } else {
        this.setStatus("reconnecting", s.error);
      }
    });
    const unAssign = window.brexDesktop.onAssignment?.((msg) => {
      for (const l of this.assignmentListeners) {
        try {
          l(msg);
        } catch {
          /* ignore */
        }
      }
      this.noteReason("live");
      this.scheduleReload();
    });
    if (unDb) this.unsubs.push(unDb);
    if (unSync) this.unsubs.push(unSync);
    if (unAssign) this.unsubs.push(unAssign);
  }

  private noteReason(reason?: string): void {
    const r = String(reason || "");
    if (!this.initialDone || CATCHUP_REASONS.has(r)) {
      this.pendingSource = "catchup";
    } else if (r === "live" && this.pendingSource !== "catchup") {
      this.pendingSource = "live";
    }
    // Unknown/apply/outbox/sync: keep pendingSource (catchup wins until consumed)
  }

  private scheduleReload(): void {
    if (this.reloadTimer) clearTimeout(this.reloadTimer);
    this.reloadTimer = setTimeout(() => {
      this.reloadTimer = null;
      void this.reload();
    }, 150);
  }

  private fingerprint(rows: AppEvent[]): string {
    if (!rows.length) return "0";
    let maxId = 0;
    let hash = 0;
    for (const e of rows) {
      if (e.id > maxId) maxId = e.id;
      const u = `${e.updatedAt || ""}|${e.status}|${contentSig(e)}`;
      for (let i = 0; i < u.length; i++) hash = (hash * 31 + u.charCodeAt(i)) | 0;
      hash = (hash * 31 + e.id) | 0;
    }
    return `${rows.length}:${maxId}:${hash}`;
  }

  private pruneSeen(keepIds: Set<number>): void {
    if (this.seen.size < 4000) return;
    for (const id of this.seen.keys()) {
      if (!keepIds.has(id)) this.seen.delete(id);
    }
  }

  private async reload(): Promise<void> {
    if (!window.brexDesktop?.listEvents) return;
    if (this.reloading) {
      this.dirty = true;
      return;
    }
    this.reloading = true;
    const myGen = ++this.gen;
    const sourceHint = !this.initialDone || this.pendingSource === "catchup" ? "catchup" : "live";
    this.pendingSource = "live";

    try {
      const { events: rows } = await window.brexDesktop.listEvents({
        limit: 2000,
        includeHidden: false,
      });
      if (myGen !== this.gen) return;

      const sorted = [...rows].sort(
        (a, b) =>
          Date.parse(a.updatedAt || "") - Date.parse(b.updatedAt || "") || a.id - b.id
      );
      const isInitial = !this.initialDone;
      const changed: AppEvent[] = [];
      for (const e of sorted) {
        const prev = this.seen.get(e.id);
        if (prev !== e.updatedAt) {
          this.seen.set(e.id, e.updatedAt);
          changed.push(e);
        }
      }
      this.initialDone = true;

      const newest = sorted.slice(-500);
      const newestIds = new Set(newest.map((e) => e.id));
      const stockKeep = sorted.filter(
        (e) =>
          !newestIds.has(e.id) &&
          e.stockStage &&
          e.stockStage !== "none" &&
          !e.hidden
      );
      this.events = [...stockKeep.slice(-500), ...newest].sort(
        (a, b) =>
          Date.parse(b.updatedAt || "") - Date.parse(a.updatedAt || "") || b.id - a.id
      );

      const keepIds = new Set(this.events.map((e) => e.id));
      this.pruneSeen(keepIds);

      const fp = this.fingerprint(this.events);
      const unchanged = fp === this.lastFingerprint;
      this.lastFingerprint = fp;

      if (changed.length && this.diffListeners.size) {
        const source: "catchup" | "live" =
          isInitial || sourceHint === "catchup" ? "catchup" : "live";
        for (const l of this.diffListeners) {
          try {
            l(changed, source);
          } catch {
            /* ignore */
          }
        }
      }

      if (!unchanged) {
        for (const l of this.listeners) {
          try {
            l(this.events);
          } catch {
            /* ignore */
          }
        }
      }
      this.setStatus(this.status === "closed" ? "open" : this.status, null);
    } catch (err) {
      this.setStatus(
        "reconnecting",
        err instanceof Error ? err.message : "Local DB read failed"
      );
    } finally {
      this.reloading = false;
      if (this.dirty) {
        this.dirty = false;
        void this.reload();
      }
    }
  }
}

let hub: DesktopEventsHub | null = null;

export function getDesktopEventsHub(): DesktopEventsHub {
  if (!hub) hub = new DesktopEventsHub();
  return hub;
}
